import React, {useEffect, useRef, useState} from 'react';

// Hover delay before the label appears. The native HTML `title` attribute has
// a browser-controlled ~2s delay that can't be configured, so we replace it
// with this.
const HOVER_DELAY_MS = 500;

const ALIGN_CLASS = {
    center: 'left-1/2 -translate-x-1/2',
    // Anchored to the trigger's right edge so the rightmost action's label
    // stays inside its card instead of sticking out past it.
    end: 'right-0',
} as const;

// Action-button tooltip. The label is only mounted while the trigger is
// hovered (after HOVER_DELAY_MS) or keyboard-focused: an always-rendered
// opacity-0 label still counts toward its scroll container's width, which
// made the Blueprints card scroll sideways with a blank band after Actions
// (deepcontext-ygo1).
const ActionTooltip: React.FC<{label: string; align?: keyof typeof ALIGN_CLASS; children: React.ReactNode}> = ({
    label,
    align = 'center',
    children,
}) => {
    const [open, setOpen] = useState(false);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const clearTimer = () => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
    };
    const show = () => {
        clearTimer();
        setOpen(true);
    };
    const hide = () => {
        clearTimer();
        setOpen(false);
    };
    const showAfterDelay = () => {
        clearTimer();
        timerRef.current = setTimeout(show, HOVER_DELAY_MS);
    };

    useEffect(() => clearTimer, []);

    return (
        <span
            className="relative inline-flex"
            onMouseEnter={showAfterDelay}
            onMouseLeave={hide}
            onFocus={show}
            onBlur={hide}
        >
            {children}
            {open && (
                <span
                    role="tooltip"
                    className={`pointer-events-none absolute bottom-full z-50 mb-1 whitespace-nowrap rounded-md bg-black px-2 py-1 text-xs font-normal text-white shadow-lg ${ALIGN_CLASS[align]}`}
                >
                    {label}
                </span>
            )}
        </span>
    );
};

export default ActionTooltip;
